import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

const isSupabaseConnected = (): boolean => {
  const url = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
  const key = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
  return Boolean(url && key && url.startsWith('https://'));
};

export default function ContactForm() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  const inquiryTypeKeys = [
    'inquiry_type1',
    'inquiry_type2',
    'inquiry_type3',
    'inquiry_type4',
    'inquiry_type5',
  ];

  const triggerWebhook = async (payload: Record<string, unknown>) => {
    if (!isSupabaseConnected()) return;
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('contact-notify', {
        body: payload,
      });
      if (invokeError) {
        console.error('[Contact] Webhook调用失败:', invokeError);
        return;
      }
      if (!data?.success) {
        console.error('[Contact] Webhook处理异常:', data?.error);
        return;
      }
      console.log('[Contact] Webhook结果:', data?.webhook_result);
    } catch (err) {
      console.error('[Contact] Webhook网络异常:', err);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const messageEl = form.elements.namedItem('message') as HTMLTextAreaElement;
    const message = messageEl.value;
    if (message.length > 500) return;

    setStatus('loading');
    setErrorMsg('');

    const name = (form.elements.namedItem('name') as HTMLInputElement).value;
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;
    const phone = (form.elements.namedItem('phone') as HTMLInputElement).value;
    const inquiryTypeSelect = form.elements.namedItem('inquiry_type') as HTMLSelectElement;
    const inquiryType = inquiryTypeSelect?.value || '';
    const companyEl = form.elements.namedItem('company') as HTMLInputElement;
    const company = companyEl?.value || '';

    // Check Supabase connection before saving
    if (!isSupabaseConnected()) {
      setErrorMsg('后端服务未连接，请稍后重试');
      setStatus('error');
      return;
    }

    // Primary: Save to Supabase
    const { error: supabaseError } = await supabase
      .from('contact_messages')
      .insert({
        name,
        email,
        phone,
        inquiry_type: inquiryType,
        company,
        message,
      });

    if (supabaseError) {
      // eslint-disable-next-line no-console
      console.error('Supabase save failed:', supabaseError);
      setErrorMsg(supabaseError.message || t('contact_form_error'));
      setStatus('error');
      return;
    }

    // Trigger notification via raw fetch (fire-and-forget)
    triggerWebhook({ name, email, phone, inquiry_type: inquiryType, company, message });

    setStatus('success');
    form.reset();
    setCharCount(0);
  };

  return (
    <div id="contact-form" className="bg-white rounded-2xl border border-[#f0ebe4] p-8 md:p-10">
      <div className="mb-8">
        <div className="text-[#C8714A] text-xs font-semibold tracking-widest uppercase mb-2">
          {t('contact_form_label')}
        </div>
        <h2 className="text-2xl font-bold text-[#1a1a1a] mb-2">{t('contact_form_title')}</h2>
        <p className="text-sm text-[#888]">{t('contact_form_desc')}</p>
      </div>

      {status === 'success' ? (
        <div className="flex flex-col items-center justify-center py-14 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#C8714A]/10 mb-4">
            <i className="ri-check-line text-3xl text-[#C8714A]"></i>
          </div>
          <h3 className="text-lg font-bold text-[#1a1a1a] mb-2">{t('contact_form_success_title')}</h3>
          <p className="text-sm text-[#888] mb-6">{t('contact_form_success_desc')}</p>
          <button
            onClick={() => setStatus('idle')}
            className="px-6 py-2.5 rounded-full bg-[#C8714A] text-white text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-[#b5603b] transition-colors"
          >
            {t('contact_form_success_btn')}
          </button>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-5"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-[#444]">
                {t('contact_form_name')} <span className="text-[#C8714A]">{t('contact_form_required')}</span>
              </label>
              <input
                type="text"
                name="name"
                required
                placeholder={t('contact_form_name_placeholder')}
                className="px-4 py-3 rounded-xl border border-[#e8e0d8] bg-[#faf8f5] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-[#444]">
                {t('contact_form_phone')} <span className="text-[#C8714A]">{t('contact_form_required')}</span>
              </label>
              <input
                type="tel"
                name="phone"
                required
                placeholder={t('contact_form_phone_placeholder')}
                className="px-4 py-3 rounded-xl border border-[#e8e0d8] bg-[#faf8f5] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-[#444]">
              {t('contact_form_email')} <span className="text-[#C8714A]">{t('contact_form_required')}</span>
            </label>
            <input
              type="email"
              name="email"
              required
              placeholder={t('contact_form_email_placeholder')}
              className="px-4 py-3 rounded-xl border border-[#e8e0d8] bg-[#faf8f5] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-[#444]">{t('contact_form_inquiry')}</label>
            <div className="relative">
              <select
                name="inquiry_type"
                className="w-full appearance-none px-4 py-3 rounded-xl border border-[#e8e0d8] bg-[#faf8f5] text-sm text-[#1a1a1a] focus:outline-none focus:border-[#C8714A] transition-colors cursor-pointer pr-10"
              >
                {inquiryTypeKeys.map((key) => (
                  <option key={key} value={t(key)}>{t(key)}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-down-s-line text-[#999]"></i>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-[#444]">
              {t('contact_form_company')} <span className="text-[#bbb] font-normal">{t('contact_form_company_optional')}</span>
            </label>
            <input
              type="text"
              name="company"
              placeholder={t('contact_form_company_placeholder')}
              className="px-4 py-3 rounded-xl border border-[#e8e0d8] bg-[#faf8f5] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-[#444]">
              {t('contact_form_message')} <span className="text-[#C8714A]">{t('contact_form_required')}</span>
            </label>
            <div className="relative">
              <textarea
                name="message"
                required
                rows={5}
                maxLength={500}
                placeholder={t('contact_form_message_placeholder')}
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full px-4 py-3 rounded-xl border border-[#e8e0d8] bg-[#faf8f5] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors resize-none"
              />
              <div className={`absolute bottom-3 right-3 text-xs ${charCount > 480 ? 'text-[#C8714A]' : 'text-[#bbb]'}`}>
                {charCount}/500
              </div>
            </div>
          </div>

          {status === 'error' && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-red-500 text-sm">
              <i className="ri-error-warning-line"></i>
              {errorMsg || t('contact_form_error')}
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'loading' || charCount > 500}
            className="w-full py-3.5 rounded-full bg-[#C8714A] text-white font-semibold text-sm hover:bg-[#b5603b] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {status === 'loading' ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                {t('contact_form_sending')}
              </>
            ) : (
              <>
                <i className="ri-send-plane-line"></i>
                {t('contact_form_submit')}
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}